import { useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  isOpenOffer,
  offerLabel,
  phaseOf,
  type QuoteListItem,
  type QuotePhase,
  type QuoteState,
  QUOTE_STATE_LABELS,
} from '@k-management/shared';
import { useNavigate } from 'react-router';
import { useCan, useCurrentUser } from '../../auth/AuthProvider.js';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { quoteKeys, quotePath, QuoteStatePill } from './quotes-ui.js';
import { useListFilters } from '../../lib/list-filters.js';

type StateFilter = 'all' | 'open' | 'expired' | 'validation' | QuoteState;

/** The states each list offers as a filter. */
const STATE_FILTERS: Record<QuotePhase, [StateFilter, string][]> = {
  preventivo: [
    ['all', 'Tutti'],
    ['validation', 'Da convalidare'],
  ],
  offerta: [
    ['all', 'Tutti gli stati'],
    ['open', 'Aperte'],
    ['da_inviare', 'Da inviare'],
    ['inviata', 'Inviate'],
    ['expired', 'Scadute'],
    ['accettata', 'Accettate'],
    ['rifiutata', 'Rifiutate'],
    ['annullata', 'Annullate'],
    ['superata', 'Versioni superate'],
  ],
};

/**
 * Area Commerciale (05/10/2026): the Preventivi, internal drafts, and the
 * Offerte for the client, two pages of the same quotes the user may see.
 */
export function QuotesListPage({ phase }: { phase: QuotePhase }) {
  const preventivi = phase === 'preventivo';
  usePageTitle(preventivi ? 'Preventivi' : 'Offerte');
  const navigate = useNavigate();
  const user = useCurrentUser();
  const canWrite = useCan('preventivatore.write');
  const quotes = useQuery({
    queryKey: quoteKeys.all,
    queryFn: () => apiRequest<QuoteListItem[]>('/quotes'),
  });
  // The filters stay while the browser is open: found again coming back to the list (1.61).
  const { filters, setFilter, reset, changed, changedClass } = useListFilters(`quotes-${phase}`, {
    state: 'all' as StateFilter,
  });
  const { state } = filters;
  const setState = (value: StateFilter) => setFilter('state', value);
  const own = user.scopes.preventivatore === 'own';

  const all = quotes.data ?? [];
  const rows = all.filter(
    (quote) =>
      phaseOf(quote.state) === phase &&
      (state === 'all' ||
        (state === 'open'
          ? quote.state === 'bozza' || (isOpenOffer(quote.state) && !quote.expired)
          : state === 'expired'
            ? quote.expired
            : state === 'validation'
              ? quote.validationPending
              : quote.state === state)),
  );

  const columns: GridColumn<QuoteListItem>[] = [
    {
      key: 'number',
      label: 'Numero',
      value: (quote) => (quote.number ? offerLabel(quote.number, quote.version) : ''),
      render: (quote) =>
        quote.number ? (
          <span className="code">{offerLabel(quote.number, quote.version)}</span>
        ) : quote.versionOf ? (
          <small className="muted">
            {offerLabel(quote.versionOf.number, quote.versionOf.version)} in preparazione
          </small>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'client',
      label: 'Cliente',
      required: true,
      value: (quote) => `${quote.clientName ?? ''} ${quote.referenceName ?? ''}`,
      render: (quote) => (
        <>
          <button type="button" className="link strong" onClick={() => navigate(quotePath(quote))}>
            {quote.clientName ?? quote.referenceName ?? 'Preventivo senza oggetto'}
          </button>
          {quote.clientName && quote.referenceName && (
            <small className="muted block">{quote.referenceName}</small>
          )}
        </>
      ),
    },
    {
      key: 'date',
      label: 'Data',
      value: (quote) => quote.date,
      render: (quote) => formatDate(quote.date),
    },
    {
      key: 'validUntil',
      label: 'Valida fino al',
      value: (quote) => quote.validUntil ?? '',
      render: (quote) => (quote.validUntil ? formatDate(quote.validUntil) : '—'),
    },
    {
      key: 'net',
      label: 'Imponibile',
      align: 'right',
      value: (quote) => Number(quote.net),
      render: (quote) => formatMoney(quote.net),
    },
    ...(own
      ? []
      : [
          {
            key: 'owner',
            label: 'Commerciale',
            value: (quote: QuoteListItem) => quote.owner?.name ?? '',
            render: (quote: QuoteListItem) => quote.owner?.name ?? '—',
          },
        ]),
    {
      key: 'state',
      label: 'Stato',
      value: (quote) => (quote.expired ? 'Scaduta' : QUOTE_STATE_LABELS[quote.state]),
      render: (quote) => (
        <>
          <QuoteStatePill state={quote.state} expired={quote.expired} />
          {quote.validationPending && <span className="pill warn">convalida chiesta</span>}
        </>
      ),
    },
  ];

  const commands: (Command | false)[] = [
    canWrite &&
      preventivi && {
        key: 'new',
        label: 'Nuovo',
        icon: 'plus',
        primary: true,
        onClick: () => navigate('/preventivatore/nuovo'),
      },
  ];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>{preventivi ? 'Preventivi' : 'Offerte'}</h1>
          <p className="muted">
            {preventivi
              ? 'Il preventivo è per uso interno, con costi e ricavi: approvato diventa un’offerta.'
              : 'Le offerte per il cliente, con il numero OFF e la versione: una nuova versione riparte dal preventivo.'}
          </p>
        </div>
      </div>
      {own && (
        <p className="notice info">
          Vedi quelli di cui sei il commerciale di riferimento e quelli che hai compilato tu.
        </p>
      )}
      {quotes.isPending && <p className="muted">Caricamento…</p>}
      {quotes.isError && <p className="error">{quotes.error.message}</p>}
      {quotes.data && (
        <DataGrid
          id={`quotes-${phase}`}
          filtersChanged={changed}
          onResetFilters={reset}
          columns={columns}
          rows={rows}
          rowKey={(quote) => quote.id}
          searchPlaceholder="Numero, cliente, oggetto"
          empty={
            !preventivi
              ? 'Nessuna offerta: nasce approvando un preventivo.'
              : canWrite
                ? 'Nessun preventivo: crea il primo con «Nuovo».'
                : 'Nessun preventivo visibile con il tuo profilo.'
          }
          rowClassName={(quote) =>
            quote.state === 'superata' || quote.state === 'annullata' ? 'inactive' : undefined
          }
          onRowOpen={(quote) => navigate(quotePath(quote))}
          toolbar={
            <>
              <select
                className={changedClass('state')}
                aria-label="Stato"
                value={state}
                onChange={(event) => setState(event.target.value as StateFilter)}
              >
                {STATE_FILTERS[phase].map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </>
          }
        />
      )}
    </section>
  );
}
